import { analysis_reference_area_enum, emission_factor_source_enum } from '@predium/enums';
import { AnyEventObject, fromCallback } from 'xstate';
import { DisplayBase } from '@/components/organisms/AnalysisSettings/machines/analysisSettingsMachine';

type UrlSyncReceivedEvent = {
  type: 'WRITE_URL';
  /** Omitted for actionPlanning scope — the machine excludes it based on context.scope. */
  displayBase?: DisplayBase;
  referenceArea: analysis_reference_area_enum;
  emissionFactorSource: emission_factor_source_enum;
  /** Portfolio / economic-unit demand analysis only. Null clears the URL param. */
  includeNewConstruction: boolean | null;
};

type UrlSyncActorInput = {
  initialDisplayBase: DisplayBase;
  initialReferenceArea: analysis_reference_area_enum;
  initialEmissionFactorSource: emission_factor_source_enum;
};

/**
 * Placeholder registered in `setup({ actors })`. Replaced at runtime via `machine.provide()`.
 *
 * This pattern (placeholder in machine + real implementation injected via provide()) keeps
 * the machine's input type clean of React/nuqs concerns.
 */
export const urlSyncActorPlaceholder = fromCallback<AnyEventObject, UrlSyncActorInput>(() => {});

/**
 * Factory that creates the real `fromCallback` URL sync actor.
 *
 * The nuqs `setQueryParams` setter is captured in a closure rather than passed via actor
 * input, so the machine's input type does not need to know about nuqs.
 *
 * On `WRITE_URL`: writes the new values to the URL. `displayBase` is optional — the machine
 * omits it for actionPlanning scope (handled via context.scope in the machine's sendTo calls).
 */
export const createUrlSyncActor = (
  setQueryParams: (
    displayBase: DisplayBase | undefined,
    referenceArea: analysis_reference_area_enum,
    emissionFactorSource: emission_factor_source_enum,
    includeNewConstruction: boolean | null,
  ) => void,
) =>
  fromCallback<AnyEventObject, UrlSyncActorInput>(({ receive }) => {
    receive((event) => {
      const e = event as unknown as UrlSyncReceivedEvent;
      if (e.type === 'WRITE_URL') {
        setQueryParams(e.displayBase, e.referenceArea, e.emissionFactorSource, e.includeNewConstruction);
      }
    });
  });
